/**
 * Pixel beacon contract — the on-the-wire schema between
 * `packages/pixel-script` (customer-installed JS) and
 * `apps/pixel-ingest` (Cloudflare Worker).
 *
 * Authoritative spec: `plans/1.0.0/pixel-ingest/02-pixel-script.md`
 * §"Beacon payload shape" and `01-tinybird.md` §"pixel_events_short".
 *
 * Single source of truth — the pixel imports `BeaconSchema` for its
 * own client-side typing; the Worker imports the same for inbound
 * validation. Drift between them is impossible.
 *
 * Conventions:
 *   - All strings cap-bounded (no unbounded fields).
 *   - All numbers finite (NaN/Infinity rejected at validation).
 *   - Optional fields use `.optional()`, not nullable — the on-wire
 *     shape omits absent fields rather than carrying explicit null.
 *   - `event_kind` is the discriminator; the rest of the event row
 *     varies by kind but is enforced loosely here (strict per-kind
 *     refinements live on the Worker side to keep this contract
 *     stable across pixel versions).
 */
import { z } from 'zod';

// ─── Limits ────────────────────────────────────────────────────────────────

/** Body must be ≤ 16 KB total (Worker enforces; this is documentation). */
export const BEACON_MAX_BODY_BYTES = 16 * 1024;

/** A single beacon may carry 1–16 events. Higher tail events from
 * particularly chatty soft-nav sites still ship under this cap by
 * flushing more often. */
export const BEACON_MAX_EVENTS = 16;

/** Event props payload cap. Worker drops props (keeps event) over this. */
export const EVENT_PROPS_MAX_BYTES = 4 * 1024;

/** Attribution selectors capped at 1 KB at the Worker. */
export const ATTRIBUTION_VALUE_MAX_BYTES = 1024;

/** Metric sanity bounds — anything outside is silently clamped. */
export const METRIC_SANITY = {
  // Page-loading metrics: milliseconds, max 60s.
  lcp_max: 60_000,
  inp_max: 30_000,
  ttfb_max: 60_000,
  fcp_max: 60_000,
  // CLS is unitless.
  cls_max: 5,
  // Long Animation Frames cap (10s — anything higher is bogus).
  loaf_max: 10_000,
} as const;

/** Money-field denylist regex — props with matching keys cause the
 * Worker to reject the beacon with HTTP 400 REVENUE_FIELD_REJECTED.
 * Revenue is webhook-led; see `cwv.checkoutRef()` for the legitimate
 * forwarding path. */
export const REVENUE_FIELD_DENY_RE = /(price|amount|value|cost|usd|eur|gbp|currency)/i;

// ─── Enums ─────────────────────────────────────────────────────────────────

/** Discriminator on every event row — see `02-pixel-script.md` and
 * the `event_kind` column on `pixel_events_short.datasource`. */
export const EVENT_KINDS = [
  'pageview', // initial hard navigation
  'pageview_soft', // SPA route change (Soft Navigation API or shim)
  'outbound_click', // click on a link with cross-origin href
  'data_track', // element with [data-track="event_name"] activated
  'custom', // cwv.track('name', props) call
  'identify', // cwv.identify() call (no metric payload)
  'visibility', // page visibility flush (CWV + engagement, not a pageview)
  'pageend', // final session flush on pagehide / visibilitychange→hidden
] as const;
export type EventKind = (typeof EVENT_KINDS)[number];

/** Form-factor enum. UA-CH + cf-device-type + UA-parse fall through
 * to one of these. No XR/EInk/Watch in 1.0.0 — those collapse to
 * `desktop` at the Worker. */
export const FORM_FACTORS = ['phone', 'tablet', 'desktop'] as const;
export type FormFactor = (typeof FORM_FACTORS)[number];

/** Channel classification — server-derived from
 * (referrer_host, utm_source, utm_medium). Rules live in
 * `apps/pixel-ingest/src/channel.ts`. */
export const CHANNELS = ['direct', 'organic', 'social', 'paid', 'referral', 'email'] as const;
export type Channel = (typeof CHANNELS)[number];

/** CWV rating buckets, computed client-side by `web-vitals` v4. */
export const METRIC_RATINGS = ['good', 'needs-improvement', 'poor'] as const;
export type MetricRating = (typeof METRIC_RATINGS)[number];

/** `web-vitals` metric names emitted by the pixel. */
export const METRIC_NAMES = ['lcp', 'cls', 'inp', 'ttfb', 'fcp'] as const;
export type MetricName = (typeof METRIC_NAMES)[number];

/** Scroll depth buckets — pixel never sends raw scroll position.
 * Bucketing prevents fine-grained user-behaviour fingerprinting. */
export const SCROLL_DEPTH_BUCKETS = [0, 25, 50, 75, 100] as const;
export type ScrollDepth = (typeof SCROLL_DEPTH_BUCKETS)[number];

/** Navigation Timing v2 `type` values we surface. */
export const NAV_TYPES = ['navigate', 'reload', 'back_forward', 'prerender'] as const;
export type NavType = (typeof NAV_TYPES)[number];

/** Attribution target kinds — see `pixel_attribution.datasource`. */
export const ATTRIBUTION_TARGET_KINDS = [
  'lcp_element',
  'inp_target',
  'cls_target',
  'lcp_url',
  'lcp_resource_type',
  'loaf_script',
] as const;
export type AttributionTargetKind = (typeof ATTRIBUTION_TARGET_KINDS)[number];

// ─── Property values ───────────────────────────────────────────────────────

/** Flat scalars allowed inside `event_props` and `identify.traits`.
 * No nesting; the Tinybird `event_props` column stores them as a
 * JSON-encoded string and the dashboard's read pipes don't dig into
 * them. */
const PropScalar = z.union([z.string().max(1024), z.number(), z.boolean()]);
export const PropsSchema = z.record(z.string().max(64), PropScalar).optional();
export type Props = z.infer<typeof PropsSchema>;

// ─── CWV metric ────────────────────────────────────────────────────────────

const CwvAttribution = z
  .record(
    z.string().max(32),
    z.union([z.string().max(ATTRIBUTION_VALUE_MAX_BYTES), z.number(), z.boolean()]),
  )
  .optional();

export const MetricSchema = z.object({
  name: z.enum(METRIC_NAMES),
  value: z.number().finite().nonnegative(),
  rating: z.enum(METRIC_RATINGS),
  attribution: CwvAttribution,
});
export type Metric = z.infer<typeof MetricSchema>;

// ─── Single beacon event ───────────────────────────────────────────────────

export const BeaconEventSchema = z.object({
  /** `bn-<10-char>` — unique per ingested row. The pixel generates one
   * per logical event; the Worker stamps it onto the Tinybird row. */
  beacon_uuid: z.string().min(13).max(13),

  /** What kind of event this row represents. See `EVENT_KINDS`. */
  event_kind: z.enum(EVENT_KINDS),

  /** User-supplied or auto-derived name. For 'pageview' it's literally
   * 'pageview'; for 'outbound_click' it's 'outbound_click'; for
   * 'data_track' it's the [data-track] attribute value; for 'custom'
   * it's `cwv.track(name)`. */
  event_name: z.string().min(1).max(128),

  /** Custom props for 'custom' / 'data_track' / 'outbound_click' events.
   * Worker enforces the money-field denylist + total ≤ 4 KB. */
  event_props: PropsSchema,

  /** ── Identify fields ─────────────────────────────────────────────
   * `profile_id` rides EVERY event once `cwv.identify()` has run, not
   * just the identify event — the pixel stamps it in `baseEvent` and
   * persists it in sessionStorage so it survives hard navigations. The
   * Worker hashes it per event into `profile_uuid`
   * (`sha256(site_id|profile_id)`, site-scoped, unsalted so the
   * customer can derive it for their own ids); the raw id is never
   * stored.
   *
   * `profile_traits` is only present on the identify event itself and
   * is persisted into that row's `event_props`. There is no traits
   * column — traits are searchable as event_props JSON on the identify
   * row, and are not carried onto later rows. */
  profile_id: z.string().min(1).max(256).optional(),
  profile_traits: PropsSchema,

  /** ── Page context ───────────────────────────────────────────────
   * Pathname only. Pixel strips query strings client-side; Worker
   * strips again defence-in-depth. */
  page_path: z.string().min(1).max(2048),

  /** ── Session position ──────────────────────────────────────────
   * The pixel stamps these client-side; the Tinybird `is_*` columns
   * match these names. */
  is_session_start: z.union([z.literal(0), z.literal(1)]).default(0),
  is_session_end: z.union([z.literal(0), z.literal(1)]).default(0),
  is_first_in_page_lifecycle: z.union([z.literal(0), z.literal(1)]).default(0),
  pageview_count_in_session: z.number().int().nonnegative().max(10_000).default(0),

  /** ── Engagement ────────────────────────────────────────────────
   * engagement_ms: visible time accrued since the previous flush.
   * scroll_depth_pct: bucketed to {0,25,50,75,100}.
   * outgoing_host: for 'outbound_click' events. */
  engagement_ms: z
    .number()
    .int()
    .nonnegative()
    .max(24 * 60 * 60_000)
    .default(0),
  scroll_depth_pct: z
    .number()
    .refine((n): n is ScrollDepth => (SCROLL_DEPTH_BUCKETS as readonly number[]).includes(n))
    .default(0),
  outgoing_host: z.string().max(255).optional(),

  /** ── Core Web Vitals (on 'pageview' / 'pageend' / 'visibility') ── */
  metrics: z.array(MetricSchema).max(8).optional(),

  /** ── Extended perf cohorts ─────────────────────────────────────
   * Each is optional — older browsers may not report all of them. */
  longest_loaf_ms: z.number().nonnegative().optional(),
  loaf_script_host: z.string().max(255).optional(),
  nav_dns_ms: z.number().nonnegative().optional(),
  nav_tcp_ms: z.number().nonnegative().optional(),
  nav_request_ms: z.number().nonnegative().optional(),
  nav_response_ms: z.number().nonnegative().optional(),
  nav_dom_interactive_ms: z.number().nonnegative().optional(),
  nav_dom_complete_ms: z.number().nonnegative().optional(),
  nav_redirect_count: z.number().int().nonnegative().max(50).optional(),
  nav_type: z.enum(NAV_TYPES).optional(),
  bfcache_hit: z.union([z.literal(0), z.literal(1)]).optional(),
  active_visible_ms: z.number().int().nonnegative().optional(),

  /** ── Timestamp ────────────────────────────────────────────────
   * ISO 8601 UTC. Worker validates within ±10 minutes of `now()` to
   * reject clock-skewed beacons (most often from misconfigured
   * automation tooling). */
  timestamp: z.string().datetime({ offset: false }),
});
export type BeaconEvent = z.infer<typeof BeaconEventSchema>;

// ─── Beacon envelope ───────────────────────────────────────────────────────

export const BeaconSchema = z.object({
  /** `site-<10-char>` — the public site id the customer pasted into
   * their snippet. Non-secret. */
  site_id: z.string().min(15).max(15),

  /** `ses-<10-char>` — generated client-side, persisted in
   * `sessionStorage`. Tab-scoped. The Worker hashes this with the
   * daily salt to derive `device_uuid`. */
  session_id: z.string().min(14).max(14),

  /** One or more events. Cap = BEACON_MAX_EVENTS. */
  events: z.array(BeaconEventSchema).min(1).max(BEACON_MAX_EVENTS),

  /** Pathname of the current page at flush time. Used for the
   * Worker-side hostname allowlist check; the per-event rows carry
   * their own `page_path`. */
  page: z.string().min(1).max(2048),

  /** Host portion of `document.referrer`, or '' for direct/same-origin. */
  referrer_host: z.string().max(255).default(''),

  /** `navigator.connection.{effectiveType,saveData}` — both optional
   * across browsers. */
  network: z
    .object({
      effectiveType: z.string().max(16).default('unknown'),
      saveData: z.boolean().default(false),
    })
    .default({ effectiveType: 'unknown', saveData: false }),

  /** Customer-supplied A/B-test tag via snippet `flag` callback or
   * `<meta name="clickweave-flag-*">`. */
  flag: z
    .object({
      dimension: z.string().max(128),
      value: z.string().max(128),
    })
    .nullable()
    .optional(),

  /** Customer-supplied deploy id via `deployId` callback or
   * `<meta name="clickweave-deploy-id">`. */
  deploy_id: z.string().max(128).nullable().optional(),

  /** `navigator.webdriver` reflected by the pixel at session init.
   * Real browsers report false; Selenium/Playwright/etc. report
   * true. Worker's bot-shape filter drops `webdriver: true` silently. */
  webdriver: z.boolean().default(false),

  /** Opt-in UTM keys — only present when the customer enabled
   * `allowUtm: [...]` in their snippet AND the site's
   * `sites.pixel_allow_utm` column lists the matching keys. */
  utm: z
    .object({
      source: z.string().max(128).optional(),
      medium: z.string().max(128).optional(),
      campaign: z.string().max(128).optional(),
      term: z.string().max(128).optional(),
      content: z.string().max(128).optional(),
    })
    .optional(),

  /** Cross-domain stitching — set on the destination pixel when the
   * source pixel called `cwv.linkFor(href)` and the destination is
   * an allowlisted hostname. Carries the upstream `session_id`. */
  inherited_session_id: z.string().min(14).max(14).optional(),
});
export type Beacon = z.infer<typeof BeaconSchema>;

// ─── Worker-side enrichment fields ─────────────────────────────────────────
//
// Not part of the on-wire schema — these are stamped server-side by the
// ingest Worker after validation. Surfaced here as a TypeScript shape so
// the Tinybird write path can share the type.

export interface BeaconEnrichment {
  /** Resolved at the Worker via KV lookup of `site_id`. */
  org_uuid: string;
  /** Resolved at the Worker via KV lookup of `site_id`. */
  site_uuid: string;
  /** Computed at the Worker:
   * `sha256(daily_salt + ip_subnet + ua_fingerprint + site_id)`,
   * truncated to 16 chars base64url. Rotates daily at UTC midnight. */
  device_uuid: string;
  /** Cloudflare `cf.country`. ISO 3166-1 alpha-2 or empty. */
  country_code: string;
  /** Cloudflare `cf.region`. Subdivision code or empty. */
  region_code: string;
  /** Cloudflare `cf-ray` colo, e.g. 'LHR', 'IAD'. */
  colo: string;
  form_factor: FormFactor;
  browser_family: string;
  browser_engine: string;
  os_family: string;
  os_major: string;
  network_type: string;
  network_save_data: 0 | 1;
  channel: Channel;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
}

// ─── Attribution row (sibling datasource) ─────────────────────────────────

export const AttributionRowSchema = z.object({
  beacon_uuid: z.string().min(13).max(13),
  session_uuid: z.string().min(14).max(14),
  org_uuid: z.string(),
  site_uuid: z.string(),
  page_path: z.string(),
  metric_name: z.enum(METRIC_NAMES),
  metric_value: z.number().finite().nonnegative(),
  metric_rating: z.enum(METRIC_RATINGS),
  target_kind: z.enum(ATTRIBUTION_TARGET_KINDS),
  /** Capped 1 KB at the Worker. */
  target_value: z.string().max(ATTRIBUTION_VALUE_MAX_BYTES),
  interaction: z.string().max(32).default(''),
  timestamp: z.string().datetime({ offset: false }),
});
export type AttributionRow = z.infer<typeof AttributionRowSchema>;

// ─── Tinybird row shape ────────────────────────────────────────────────────
//
// What the Worker writes to `pixel_events_short` / `pixel_events_long` via
// `/v0/events`. Matches the SCHEMA block in
// `pixel_events_short.datasource`. Exported as a TS shape (not a zod
// schema) because the Worker constructs these directly; there's no
// untrusted input on the write path.

export interface PixelEventRow {
  beacon_uuid: string;
  session_uuid: string;
  device_uuid: string;
  profile_uuid: string;
  org_uuid: string;
  site_uuid: string;

  event_kind: EventKind;
  event_name: string;
  event_props: string; // JSON-encoded string; '' when no props

  page_uuid: string; // '' when URL isn't promoted to a tracked page
  page_path: string;

  is_session_start: 0 | 1;
  is_session_end: 0 | 1;
  is_first_in_page_lifecycle: 0 | 1;
  pageview_count_in_session: number;

  country_code: string;
  region_code: string;
  colo: string;
  form_factor: FormFactor;
  browser_family: string;
  browser_engine: string;
  os_family: string;
  os_major: string;
  network_type: string;
  network_save_data: 0 | 1;

  referrer_host: string;
  channel: Channel;

  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;

  engagement_ms: number;
  scroll_depth_pct: ScrollDepth;
  outgoing_host: string;

  lcp: number | null;
  lcp_rating: string;
  cls: number | null;
  cls_rating: string;
  inp: number | null;
  inp_rating: string;
  ttfb: number | null;
  ttfb_rating: string;
  fcp: number | null;
  fcp_rating: string;

  longest_loaf_ms: number | null;
  loaf_script_host: string;

  /** ── LCP breakdown ───────────────────────────────────────────────
   * web-vitals hands the Worker four sub-part timings and the LCP
   * resource's own PerformanceResourceTiming entry. Together they say
   * whether the largest element was slow because the server was slow,
   * because the image was discovered late, because the image was big,
   * or because rendering was blocked — the four fixes are unrelated,
   * so a bare LCP number can't tell you which one to make.
   *
   * Numeric here, identifier in `pixel_attribution` (`lcp_url`) —
   * the same split LoAF already uses. Null on every row except the
   * web-vitals beacon that carried an LCP metric. */
  lcp_resource_bytes: number | null;
  lcp_ttfb_ms: number | null;
  lcp_resource_load_delay_ms: number | null;
  lcp_resource_load_ms: number | null;
  lcp_render_delay_ms: number | null;

  nav_dns_ms: number | null;
  nav_tcp_ms: number | null;
  nav_request_ms: number | null;
  nav_response_ms: number | null;
  nav_dom_interactive_ms: number | null;
  nav_dom_complete_ms: number | null;
  nav_redirect_count: number;
  nav_type: string;
  bfcache_hit: 0 | 1;
  active_visible_ms: number;

  flag_dimension: string;
  flag_value: string;
  deploy_id: string;
  source: 'pixel'; // reserved column; always 'pixel' in 1.0.0

  timestamp: string; // ISO 8601 UTC
}

/** Plan tier slug — controls which datasource the Worker routes to. */
export const PLAN_TIER_SLUGS = ['indie', 'startup', 'pro', 'business'] as const;
export type PlanTierSlug = (typeof PLAN_TIER_SLUGS)[number];

/** Maps `plan_slug` → datasource name for tier-scaled retention.
 * Indie + Startup → `pixel_events_short` (30 days).
 * Pro + Business → `pixel_events_long` (12 months). */
export function pixelEventsDatasourceFor(
  planSlug: PlanTierSlug,
): 'pixel_events_short' | 'pixel_events_long' {
  return planSlug === 'pro' || planSlug === 'business' ? 'pixel_events_long' : 'pixel_events_short';
}

/** Per-site daily event ceiling by plan tier (from
 * `03-pixel-ingest-worker.md` §6). The Worker silently drops
 * beacons (204) once the count is exceeded. */
export const PIXEL_DAILY_EVENT_CEILINGS: Record<PlanTierSlug, number> = {
  indie: 100_000,
  startup: 500_000,
  pro: 5_000_000,
  business: 50_000_000,
} as const;

/** Default sample rate when the customer hasn't configured one
 * via snippet or dashboard slider. From
 * `00-overview.md` §"Sampling and billing" + Q5 in
 * `PRICING-IMPACT.md` (closed 2026-05-28). */
export const PIXEL_DEFAULT_SAMPLE_RATE = 0.5;

/**
 * Per-site config the pixel ingest Worker reads from KV on every beacon,
 * keyed by `siteKvKey(site_id)`. Written by the management API on site
 * create/update/delete (write-through in `apps/api`); read by
 * `apps/pixel-ingest/src/lib/site-lookup.ts`. A KV miss makes ingest
 * 404 the beacon, so this record is what "registers" a site for RUM.
 *
 * Single source of truth for the shape — both the writer and the reader
 * import it here so they can't drift.
 */
export interface SiteKvRecord {
  org_uuid: string;
  site_uuid: string;
  /** Origin-header allowlist. Host only (no scheme), lowercase. */
  hostnames: string[];
  sample_rate: number;
  plan_slug: PlanTierSlug;
  allow_utm: string[];
  cross_domain: string[];
  /** Tombstone — the reader treats `true` the same as a KV miss. */
  deleted: boolean;
}

/** KV key prefix + builder for the per-site pixel config record. */
export const SITE_KV_PREFIX = 'pixel:site:';
export function siteKvKey(siteId: string): string {
  return `${SITE_KV_PREFIX}${siteId}`;
}

// ─── CWV rating thresholds (from web-vitals v4 — duplicated here for
//     server-side validation, MUST stay in sync) ────────────────────────────

export const CWV_THRESHOLDS = {
  lcp: { good: 2500, poor: 4000 },
  inp: { good: 200, poor: 500 },
  cls: { good: 0.1, poor: 0.25 },
  fcp: { good: 1800, poor: 3000 },
  ttfb: { good: 800, poor: 1800 },
} as const;

/** Compute the rating bucket for a given metric value. Returns `''`
 * for `null` / `undefined` / `NaN` so the column stays a
 * LowCardinality(String) with the empty sentinel. Strict bounds
 * match `web-vitals` v4's `getRating()` exactly. */
export function ratingFor(name: MetricName, value: number | null | undefined): MetricRating | '' {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';
  const t = CWV_THRESHOLDS[name];
  if (value <= t.good) return 'good';
  if (value <= t.poor) return 'needs-improvement';
  return 'poor';
}
